import { useMemo } from "react";
import { useAddDeviceToNotificationGroupMutation, useAddDeviceToProfileMutation, useGetThresholdAllUnmappedDevicesQuery, useGetThresholdMappedDevicesQuery, useGetThresholdUnmappedDevicesQuery, useRemoveDeviceFromProfileMutation, useRemoveDevicesFromNotificationGroupMutation } from "../../../store/api/admin/adminApiSlice";
import { useAddLabelDevicesMutation, useAddProfilesToDeviceMutation, useGetAllDevicesByGroupQuery, useGetNodesQuery, useGetThresholdAlertsQuery, useGetThresholdCrossingAllUnmapProfileDataQuery, useGetThresholdCrossingMapProfileDataQuery, useGetThresholdCrossingUnmapProfileDataQuery, useRemoveLabelDevicesMutation, useRemoveProfilesFromDeviceMutation } from "../../../store/api/devices/deviceApiSlice";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useSelector } from "react-redux";
import { urlAddParameterSelector as labelUrlAddParameterSelector, urlRemoveParameterSelector as labelUrlRemoveParameterSelector } from "../../../store/filters/devices/Label/LabelDeviceFilterSelectors";
import { urlAddParameterSelector as notificationUrlAddParameterSelector, urlRemoveParameterSelector as notificationUrlRemoveParameterSelector } from "../../../store/filters/devices/NotificationDeviceFilterSelectors";
import { IFilter, IFilterSelectBox } from "../Filter/types";
import { ActionCreatorWithPayload } from "@reduxjs/toolkit";

import { SxProps } from "@mui/material";
import { urlAddParameterSelector as thresholdUrlAddParameterSelector, urlRemoveParameterSelector as thresholdRemoveParameterSelector, urlAlertParameterSelector as thresholdAlertParameterSelector } from "../../../store/filters/profiles/ThresholdCrossingDeviceFilterSelectors";
import { API } from "../../../globals/globals";

export interface IAssociationAddProps {
  addFilters?: IFilter;
  addColumns?: any[];
  addSorting?: string;
  addResetFilterHandler?: (state: any) => void;
  showAddAction?: boolean;
  showAddAll?: boolean;
  addCheckboxSelection?: boolean;
  addDisableColumnMenu?: boolean;
  addDisableRowSelectionOnClick?: boolean;
  addMutationKey?: any;
  addMutationPayloadKey?: any[];
  urlAddParameterKey?: string;
  loadAddDataKey?: string;
  loadAddAllDataKey?: string;
  addShowInfoMessage?: boolean;
  addInfoMessage?: string;
  addRowSelectable?: (param: any) => boolean;
  addSetRowClass?: (param: any) => string;
  disabledAddActionButton?: (param: any) => boolean;
  sxAdd?: SxProps;
  isShowRemovePanelNotification?: boolean;
  notificationMessageforRemove?: string;
}
// Define sets for keys associated with different data loaders
const nodeKeys = new Set([
  "getNotificationMapProfile",
  "getNotificationUnmapProfile",
  "getLabelMapProfile",
  "getLabelUnmapProfile",
]);
// Keys for all devices by group loader
const allDevicesKeys = new Set([
  "getNotificationUnmapAllProfile",
  "getLabelUnmapAllProfile",
]);

const thresholdMappedDevicesKeys = new Set([
  "getThresholdMapped",
]);

const thresholdUnmappedDevicesKeys = new Set([
  "getThresholdUnmapped",
]);

const thresholdAllUnmappedDevicesKeys = new Set([
  "getThresholdAllUnmapped",
]);


const thresholdCrossingMapProfileKeys = new Set([
  "getThresholdCrossingMapProfile",
]);

const thresholdCrossingUnmapProfileKeys = new Set([
  "getThresholdCrossingUnmapProfile",
]);

const thresholdCrossingAllUnmapProfileKeys = new Set([
  "getThresholdCrossingAllUnmapProfile",
])

const thresholdAlertsKeys = new Set([
  "getThresholdAlerts",
])
// Hook to load data based on the key
export const useLoadData = (key: string, args: any) => {
  const nodesQuery = useGetNodesQuery(
    nodeKeys.has(key) ? args : skipToken
  );

  const allDevicesQuery = useGetAllDevicesByGroupQuery(
    allDevicesKeys.has(key) ? args : skipToken
  );
  
  const thresholdMappedDevicesQuery = useGetThresholdMappedDevicesQuery(
    thresholdMappedDevicesKeys.has(key) ? args : skipToken
  );

  const thresholdUnmappedDevicesQuery = useGetThresholdUnmappedDevicesQuery(
    thresholdUnmappedDevicesKeys.has(key) ? args : skipToken
  );

  const thresholdAllUnmappedDevicesQuery = useGetThresholdAllUnmappedDevicesQuery(
    thresholdAllUnmappedDevicesKeys.has(key) ? args : skipToken
  );


  const thresholdCrossingMapProfileQuery = useGetThresholdCrossingMapProfileDataQuery(
    thresholdCrossingMapProfileKeys.has(key) ? args : skipToken
  );

  const thresholdCrossingUnmapProfileQuery = useGetThresholdCrossingUnmapProfileDataQuery(
    thresholdCrossingUnmapProfileKeys.has(key) ? args : skipToken
  );

  const thresholdCrossingAllUnmapProfileQuery = useGetThresholdCrossingAllUnmapProfileDataQuery(
    thresholdCrossingAllUnmapProfileKeys.has(key) ? args : skipToken
  );


  const thresholdAlertQuery = useGetThresholdAlertsQuery(
    thresholdAlertsKeys.has(key) ? args : skipToken,{
      pollingInterval: API.ENDPOINTS.THRESHOLDALERT.pollingInterval || API.POLLING_INTERVAL,
    }
  );


  if (nodeKeys.has(key)) return nodesQuery;
  if (allDevicesKeys.has(key)) return allDevicesQuery;
  if (thresholdMappedDevicesKeys.has(key)) return thresholdMappedDevicesQuery;
  if (thresholdUnmappedDevicesKeys.has(key)) return thresholdUnmappedDevicesQuery;
  if (thresholdAllUnmappedDevicesKeys.has(key)) return thresholdAllUnmappedDevicesQuery;
  if (thresholdCrossingMapProfileKeys.has(key)) return thresholdCrossingMapProfileQuery;
  if (thresholdCrossingUnmapProfileKeys.has(key)) return thresholdCrossingUnmapProfileQuery;
  if (thresholdCrossingAllUnmapProfileKeys.has(key)) return thresholdCrossingAllUnmapProfileQuery;    
  if (thresholdAlertsKeys.has(key)) return thresholdAlertQuery;
  throw new Error(`No unmapped data loader found for key: ${key}`);
};


// Map keys to their corresponding URL parameter selectors
const urlParameterSelectorMap = {
  getNotificationMapProfile: notificationUrlRemoveParameterSelector,
  getNotificationUnmapProfile: notificationUrlAddParameterSelector,

  getLabelMapProfile: labelUrlRemoveParameterSelector,
  getLabelUnmapProfile: labelUrlAddParameterSelector,

  getThresholdMapped :thresholdRemoveParameterSelector ,
  getThresholdUnmapped : thresholdUrlAddParameterSelector,
  getThresholdAlerts: thresholdAlertParameterSelector,
  
} as const;

// Hook to get URL parameters based on the key
// export const useUrlParameter = (key: string) => {

//   if (!(key in urlParameterSelectorMap)) {
//     throw new Error(`No unmapped data loader found for key: ${key}`);
//   }
//   const selector = urlParameterSelectorMap[key as keyof typeof urlParameterSelectorMap];
//   return useSelector(selector);
// };
export const useUrlParameter = (key: string) => {
  const selector =
    key && key in urlParameterSelectorMap
      ? urlParameterSelectorMap[key as keyof typeof urlParameterSelectorMap]
      : () => new URLSearchParams();

  return useSelector(selector);
};

// Function to create payload from keys and values
export const createPayload = (keys: any[], values: any[]): Record<string, any> => {
  if (keys.length !== values.length) {
    throw new Error("Keys and values arrays must have the same length");
  }
  const payload: Record<string, any> = {};
  keys.forEach((key, index) => {
    payload[key] = values[index];
  });
  return payload;
};

// Define the payload keys for each mutation
export const payloadKeyList: any = {
  addNotificationProfile: ["groupKey", "deviceKeys"],
  removeNotificationProfile: ["groupKey", "deviceKeys"],
  addLabelToDevice: ["labelName", "deviceKeys"],
  removeLabelFromDevice: ["labelName", "deviceKeys"],
  addThresholdDevices: ["profileKey", "deviceKeys"],
  removeThresholdDevices: ["id", "deviceKeys"],

  addThresholdProfileDevices: ["deviceKey", "profilesKeys"],
  removeThresholdProfileDevices: ["deviceKey", "profilesKeys"],
};



// Define the mutation registry type
type MutationRegistry = {
  addNotificationProfile: (payload: any) => Promise<any>;
  removeNotificationProfile: (payload: any) => Promise<any>;
  addLabelToDevice: (payload: any) => Promise<any>;
  removeLabelFromDevice: (payload: any) => Promise<any>;
  addThresholdDevices: (payload: any) => Promise<any>;
  removeThresholdDevices: (payload: any) => Promise<any>;
  addThresholdProfileDevices: (payload: any) => Promise<any>;
  removeThresholdProfileDevices: (payload: any) => Promise<any>;
};

export function getFilter(
  id: string,
  label: string,
  value: string,
  setValue: ActionCreatorWithPayload<any | string[], string>,
  values: IFilterSelectBox["values"],
  width: string = "200px"
): IFilterSelectBox {
  return {
    id,
    label,
    value,
    setValue,
    values,
    width,
  };
}

// Define the mutation key type
type MutationKey = keyof MutationRegistry;
export const useAssociationMutations = () => {
  const [addDeviceToNotificationGroup] = useAddDeviceToNotificationGroupMutation();
  const [removeDevicesFromNotificationGroup] = useRemoveDevicesFromNotificationGroupMutation();
  const [removeLabelDevices] = useRemoveLabelDevicesMutation();
  const [addLabelDevices] = useAddLabelDevicesMutation();
  const [addDeviceToProfile] = useAddDeviceToProfileMutation();
  const [removeDeviceFromProfile] = useRemoveDeviceFromProfileMutation();
  const [addProfilesToDevice] = useAddProfilesToDeviceMutation();
  const [removeProfilesFromDevice] = useRemoveProfilesFromDeviceMutation();

  // Create the mutation registry
  const registry: MutationRegistry = useMemo(
    () => ({
      addNotificationProfile: addDeviceToNotificationGroup,
      removeNotificationProfile: removeDevicesFromNotificationGroup,
      addLabelToDevice: addLabelDevices,
      removeLabelFromDevice: removeLabelDevices,
      addThresholdDevices: addDeviceToProfile,
      removeThresholdDevices: removeDeviceFromProfile,
      addThresholdProfileDevices: addProfilesToDevice,
      removeThresholdProfileDevices: removeProfilesFromDevice,
    }),
    [addDeviceToNotificationGroup, removeDevicesFromNotificationGroup, addLabelDevices, removeLabelDevices,addDeviceToProfile, removeDeviceFromProfile, addProfilesToDevice, removeProfilesFromDevice]
  );

  // Function to execute a mutation based on the key
  const executeMutation = <K extends MutationKey>(
    key: K,
    payload: Parameters<MutationRegistry[K]>[0]
  ): Promise<Awaited<ReturnType<MutationRegistry[K]>>> => {
    const mutationFn = registry[key];

    if (!mutationFn) {
      throw new Error(`Mutation "${String(key)}" not found`);
    }

    return mutationFn(payload);
  };
  return { executeMutation, payloadKeyList, createPayload };
};